import { Location } from '@angular/common';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { CompanyService } from 'src/app/core/services/company.service';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  AfterViewInit,
  Output,
  ViewChild,
  inject,
  effect,
  signal,
  ChangeDetectorRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { filter } from 'rxjs/operators';

import { SnackbarService } from '~/core/services/snackbar.service';
import { StorageService } from '~/core/services/storage.service';
import { AnalyticsService } from '~/core/services/analytics.service';
import { NavigationService, ParamsActions } from '~/core/services/navigation.service';
import { GuestUserDepositService } from '~/core/services/guest-user-deposit.service';
import { PaymentProviders } from '~/core/enum/paymentProviders';
import { AuthenticationField } from '~/core/models/authentication-field';
import { HelperPlus2Service } from '~/core/services/helper-plus2.service';
import { GamificationProviderService } from '~/core/services/gamification-provider.service';
import { features } from '~/core/constants/features';
import { CookiePermissionService } from '~/core/services/cookie-permission.service';
import { CurrencyCasePipe } from '~/core/helpers/currency-case-pipe/currency-case-pipe';
import { MemoryCleanupService } from '~/core/services/memory-cleanup.service';
import { UtilsService } from '~/core/helpers/utils';
import { getFirstName } from '~/core/helpers/string-utils';
import { UserStore } from '~/core/store/user.store';
import { CompanyStore } from '~/core/store/company.store';
import { formatNumber, getAvatarUrl } from '~/core/services/smartico/smartico-utils';
import {
  SmarticoService,
  SmarticoUserProfile,
  SmarticoUserService,
  SmarticoLevel,
  SmarticoGamificationService,
} from '~/core/services/smartico';
import { calculateUserLevel } from '~/core/services/smartico/smartico-utils';
import { CookieService } from '~/core/services/cookie.service';
import { SkeletonLoaderPlus2Component } from '~/components/skeleton-loader-plus2/skeleton-loader-plus2.component';
import { GamificationNotificationsComponent } from '~/components/gamification-notifications/gamification-notifications.component';
import { AuthenticationInfoComponent } from '~/components/authentication-info/authentication-info.component';
import { SkeletonTypes } from '~/core/services/quotation-data-service/models/quotation-enums';
import { AuthScreenPreloadService } from '~/core/services/auth-screen-preload.service';

@Component({
  selector: 'app-authentication-field',
  templateUrl: './authentication-field.component.html',
  styleUrls: ['./authentication-field.component.scss'],
  imports: [
    MatButton,
    TranslatePipe,
    MatIcon,
    MatMenuModule,
    MatProgressSpinnerModule,
    CurrencyCasePipe,
    AuthenticationInfoComponent,
    SkeletonLoaderPlus2Component,
    GamificationNotificationsComponent,
  ],
})
export class AuthenticationFieldComponent extends AuthenticationField implements OnInit, AfterViewInit, OnDestroy {
  dialog: MatDialog;
  authentication: AuthenticationService;
  companyService: CompanyService;
  router: Router;
  route: ActivatedRoute;
  translate: TranslateService;

  helper = inject(HelperPlus2Service);
  snackbarService: SnackbarService;
  storageService: StorageService;
  analyticsService = inject(AnalyticsService);
  protected navigation: NavigationService;
  protected guestUserDepositService = inject(GuestUserDepositService);
  protected location: Location;
  protected cookiesService: CookiePermissionService;
  protected cookies: CookieService = inject(CookieService);
  smarticoService = inject(SmarticoService);
  smarticoUserService = inject(SmarticoUserService);
  smarticoGamificationService = inject(SmarticoGamificationService);
  gamificationProviderService = inject(GamificationProviderService);
  cdr = inject(ChangeDetectorRef);
  private authScreenPreload = inject(AuthScreenPreloadService);

  @ViewChild('authenticationInfoComponent') authenticationInfoComponent: AuthenticationInfoComponent;
  @ViewChild(GamificationNotificationsComponent) notificationsComponent!: GamificationNotificationsComponent;
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

  private destroy$ = new Subject<void>();
  protected readonly userStore: UserStore = inject(UserStore);
  protected readonly companyStore: CompanyStore = inject(CompanyStore);
  hasDepositCreateCompany: boolean;

  get hasPanelPermission() {
    return this.authentication.hasPermission(features.PANEL);
  }

  constructor() {
    const dialog = inject(MatDialog);
    const authentication = inject(AuthenticationService);
    const companyService = inject(CompanyService);
    const router = inject(Router);
    const route = inject(ActivatedRoute);
    const translate = inject(TranslateService);
    const snackbarService = inject(SnackbarService);
    const storageService = inject(StorageService);
    const navigation = inject(NavigationService);
    const location = inject(Location);
    const cookiesService = inject(CookiePermissionService);
    super(
      dialog,
      authentication,
      companyService,
      router,
      route,
      snackbarService,
      translate,
      navigation,
      location,
      cookiesService,
      storageService,
    );
    this.dialog = dialog;
    this.authentication = authentication;
    this.companyService = companyService;
    this.router = router;
    this.route = route;
    this.translate = translate;
    this.snackbarService = snackbarService;
    this.storageService = storageService;
    this.navigation = navigation;
    this.location = location;
    this.cookiesService = cookiesService;

    this.company = this.companyStore.companySignal();
    this.updateUserBalance();
    this.isLoggedIn = !!this.cookies.getToken();

    effect(() => {
      const user = this.userStore.userSignal();

      if (user) {
        this.isLoggedIn = true;
        this.isAuthLoading.set(false);
        this.isGuestPlayer = this.authentication.isGuestPlayer();
        this.updateUserBalance();
      } else if (!this.cookies.getToken() || !user) {
        this.isAuthLoading.set(false);
        this.buttonsHide = true;
        this.shouldUseRecaptchaV2 = false;
        this.showRecaptcha = false;
        this.loginOpen = true;
        this.isLoggedIn = false;
        this.isGuestPlayer = false;
        this.isRegistrationIncomplete = this.authentication.isRegistrationIncomplete();
      }
      this.cdr.markForCheck();
    });

    this.subscriptions.push(
      this.authentication.logoutEvent.subscribe(() => {
        this.dialog.closeAll();
        this.isLoggedIn = false;
        this.cdr.markForCheck();
      }),
    );

    this.subscriptions.push(
      this.guestUserDepositService.loadingPaybrokers.subscribe((isLoading) => {
        this.openingGuestDeposit = isLoading;
        this.cdr.markForCheck();
      }),
    );

    this.subscriptions.push(
      this.authentication.updateUserEvent.subscribe(() => {
        this.isRegistrationIncomplete = this.authentication.isRegistrationIncomplete();
        MemoryCleanupService.setTimeout(
          'refreshBalance',
          () => {
            this.updateUserBalance();
            this.loadingRefresh = false;
            this.cdr.markForCheck();
          },
          2000,
        );
      }),
    );

    this.subscriptions.push(
      this.smarticoService
        .getState()
        .pipe(
          filter(
            (state) =>
              state.isReady && this.smarticoService?.isGamificationEnabled() && this.smarticoUserService?.getIsUserGamificationEnabled(),
          ),
          takeUntil(this.destroy$),
        )
        .subscribe((value) => {
          this.isSmarticoReady = value.isReady;
          this.cdr.markForCheck();
        }),
    );

    this.subscriptions.push(
      this.smarticoUserService.getUserProfile$().subscribe((profile) => {
        this.profile = profile;
        this.cdr.markForCheck();
      }),
    );

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.guestDepositOpened =
        params.action === ParamsActions.GUEST_DEPOSIT &&
        (this.companyService.guestPlayerPaymentProvider === PaymentProviders.PAGFAST ||
          this.companyService.guestPlayerPaymentProvider === PaymentProviders.PAYBROKERS_SOLUTIONS);
      this.cdr.markForCheck();
    });
  }

  get hasCasinoGameOpened() {
    return this.navigation.hasCasinoGameOpened;
  }

  menuOpenedClass = false;

  error;
  loginOpen = true;
  buttonsHide = true;
  isLoading;
  loadingRefresh = false;
  openingGuestDeposit = false;
  isOpeningLogin = signal(false);
  isOpeningRegister = signal(false);
  isOpeningDeposit = signal(false);
  isAuthLoading = signal(!!this.cookies.getToken());
  company;
  isRegisterClientEnable = false;
  isRegistrationIncomplete = false;
  loginWithGuest = false;
  userBalance: number;
  userBalanceWithBonus: number;
  guestDepositOpened = false;
  userFirstName: string;
  isSmarticoReady: boolean = false;
  levels: SmarticoLevel[] = [];
  public profile: SmarticoUserProfile;

  public currentLevel: SmarticoLevel | null = null;
  public progressPercentage: number = 0;

  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
  @Output() toggleEvent: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('recaptchaV2') recaptchaV2: any;
  protected subscriptions: Array<Subscription> = [];
  public readonly UtilsService = UtilsService;
  protected readonly skeletonTypes = SkeletonTypes;

  protected get hasDepositCreate() {
    return !!this.companyStore.configSignal()?.client?.features?.deposit?.create;
  }

  ngOnInit() {
    super.init();
    this.isRegisterClientEnable = this.checkIfIsRegisterClientEnable();
    this.isRegistrationIncomplete = this.authentication.isRegistrationIncomplete();
    this.hasDepositCreateCompany = CompanyService.hasWithdrawCreateCompany;
    this.updateUserBalance();

    this.subscriptions.push(
      this.smarticoService
        .getState()
        .pipe(filter((state) => state.isReady && this.smarticoService.isGamificationEnabled()))
        .subscribe(() => {
          this.fetchLevels();
        }),
    );

    this.subscriptions.push(
      this.smarticoUserService.getUserProfile$().subscribe((profile) => {
        this.profile = profile;
        this.calculateLevelProgress();
      }),
    );

    this.subscriptions.push(
      this.helper.toggleLoginMenu.subscribe(() => {
        this.toggleLoginEvent();
      }),
    );
    this.subscriptions.push(
      this.helper.closeLoginMenu.subscribe(() => {
        this.trigger.closeMenu();
      }),
    );
    if (this.helper.openLoginForCasinoLive) {
      MemoryCleanupService.setTimeout(
        'handleCasinoLiveLoginTrigger',
        () => {
          this.toggleLoginEvent();
          this.helper.openLoginForCasinoLive = false;
          this.cdr.markForCheck();
        },
        500,
      );
    }
  }

  ngAfterViewInit() {
    if (this.trigger) {
      this.trigger.restoreFocus = false;
      this.subscriptions.push(
        this.trigger.menuOpened.subscribe(() => {
          setTimeout(() => {
            const menuPanel = document.querySelector('.left-menu .mat-mdc-menu-content');
            if (menuPanel) {
              menuPanel.scrollTop = 0;
              this.cdr.markForCheck();
            }
          }, 0);
        }),
      );
    }
  }

  async fetchLevels() {
    if (this.isSmarticoEnabled) {
      const levels = await this.smarticoGamificationService.getLevels();
      this.levels = levels.sort((a, b) => a.required_points - b.required_points);
      this.calculateLevelProgress();
    }
  }

  calculateLevelProgress() {
    if (this.levels.length && this.profile) {
      const calculation = calculateUserLevel(this.levels, this.profile);
      this.currentLevel = calculation.currentLevel;
      this.progressPercentage = calculation.progressPercentage;
      this.cdr.markForCheck();
    }
  }

  get isSmarticoEnabled() {
    return this.isSmarticoReady;
  }

  get smarticoDisplayName() {
    if (this.isSmarticoEnabled && this.profile?.public_username) {
      return getFirstName(this.profile.public_username);
    }
    return this.userFirstName;
  }

  get shouldShowGamificationProfile() {
    return this.gamificationProviderService.isAnyGamificationEnabled();
  }

  /**
   * Returns true while we're waiting for Smartico to confirm it's ready.
   * For non-smartico providers (e.g. Promofy), resolves immediately to false
   * so the skeleton disappears and the default icon is shown.
   */
  get isAvatarLoading(): boolean {
    return this.gamificationProviderService.isSmarticoGamificationEnabled() && !this.isSmarticoEnabled;
  }

  openAvatarWidget() {
    this.smarticoGamificationService.openAvatarWidget(true, true);
  }

  openRegister(isClick?: boolean): void {
    if (this.isOpeningRegister()) return;

    this.preloadRegisterDialog();
    this.isOpeningRegister.set(true);

    if (this.companyStore.isGlobalRegistrationMode()) {
      this.router.navigate(['register-user']).then(() => {
        this.isOpeningRegister.set(false);
      });
    } else {
      this.authentication.openRegisterScreen(this.location, isClick).then(() => {
        this.isOpeningRegister.set(false);
      });
    }
  }

  openRegisterDialog(): void {}

  preloadRegisterDialog(): void {
    if (!this.isDesktop) return;

    void this.authScreenPreload.loadRegisterDialog().catch(() => undefined);
  }

  toggleLoginEvent() {
    if (this.isGuestPlayer) {
      this.loginWithGuest = true;
    }
    if (!this.isLoggedIn) {
      this.authentication.openSignInScreen(this.location);
    } else {
      this.toggleEvent.emit();
      this.trigger.toggleMenu();
    }
    this.cdr.markForCheck();
  }

  toggleMenuClassOpened() {
    this.menuOpenedClass = !this.menuOpenedClass;
    this.changeButtonsHide();
  }

  changeButtonsHide(value?: boolean) {
    if (value !== undefined) {
      this.buttonsHide = value;
    } else {
      this.buttonsHide = !this.buttonsHide;
    }
    this.showAuth = false;

    if (!this.buttonsHide) {
      setTimeout(() => {
        this.authenticationInfoComponent?.focusOnInput();
      }, 500);
    }
  }

  openLoginDialog() {
    if (this.isOpeningLogin()) return;

    this.preloadLoginDialog();
    this.isOpeningLogin.set(true);
    this.authentication.openSignInScreen(this.location).then(() => {
      this.isOpeningLogin.set(false);
    });
  }

  preloadLoginDialog(): void {
    if (!this.isDesktop) return;

    void this.authScreenPreload.loadLoginDialog().catch(() => undefined);
  }

  override openLoginDialogDeposit(): void {
    if (this.isOpeningDeposit()) return;

    this.isOpeningDeposit.set(true);
    void this.routedDialogService.openDeposit().finally(() => {
      this.isOpeningDeposit.set(false);
    });
  }

  checkIfIsRegisterClientEnable() {
    const company = this.companyStore.companySignal();
    if (company) {
      return company.client_registration;
    }
  }

  refresh() {
    this.loadingRefresh = true;
    this.authentication.getUserBonus();
    if (this.loadingRefresh) {
      this.authentication.refreshBalance();
    }

    MemoryCleanupService.setTimeout(
      'refreshBalance',
      () => {
        this.updateUserBalance();
        this.loadingRefresh = false;
      },
      2000,
    );
  }

  ngOnDestroy() {
    super.destroy();
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
    MemoryCleanupService.clearTimer('refreshBalance');
    MemoryCleanupService.clearTimer('handleCasinoLiveLoginTrigger');
    MemoryCleanupService.clearTimer('resetLoginLoading');
    MemoryCleanupService.clearTimer('resetRegisterLoading');
    MemoryCleanupService.clearTimer('resetDepositLoading');
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateUserBalance() {
    const currentUser = this.userStore.userSignal();
    this.userFirstName = getFirstName(currentUser?.name);
    this.userBalance = this.getUserBalance();
    this.userBalanceWithBonus = this.getUserBalanceWithBonus();
  }

  navigateToGuestDeposit(): void {
    this.analyticsService.guestOpeningScreen(this.companyStore.companySignal());
    this.navigation.openRequestGuestDeposit();
  }

  /**
   * Open notifications drawer
   * Called by child components (e.g., gamification-menu)
   * Closes the authentication-info menu and reopens it when drawer closes
   */
  openNotificationsDrawer(): void {
    if (this.notificationsComponent) {
      // Close the mat-menu
      if (this.menuTrigger) {
        this.menuTrigger.closeMenu();
      }

      // Open the notifications drawer
      this.notificationsComponent.open();

      // Subscribe to drawer close event to reopen menu
      // We'll check when isOpen becomes false
      const checkInterval = setInterval(() => {
        if (!this.notificationsComponent.isOpen()) {
          // Drawer is closed, reopen the menu
          if (this.menuTrigger) {
            this.menuTrigger.openMenu();
          }
          clearInterval(checkInterval);
        }
      }, 100);

      // Clear interval after 30 seconds to prevent memory leak
      setTimeout(() => clearInterval(checkInterval), 30000);
    }
  }

  protected readonly getAvatarUrl = getAvatarUrl;
  protected readonly formatNumber = formatNumber;
}
